/* ============================================================================
   LiquidLayer.tsx — renderer ASCII liquide pour le glyph set `liquid`.

   Inspiré Unseen Studio "2025 Wrapped" (scan CX 2026-05-12 TOP 10 #5) :
   chaque caractère de la grille est rendu sur canvas 2D avec un offset
   continu généré par des fonctions sin/cos modulées en temps, produisant
   un effet de vague liquide qui parcourt l'ensemble.

   Différence vs <pre> ASCII statique : le rendu est ANIMÉ (RAF loop),
   chaque cellule a son propre offset (wavex, wavey) qui évolue avec le
   temps + sa position dans la grille → propagation diagonale de la vague.

   Différence vs StippleLayer/HalftoneLayer (dots) : ici on dessine les
   GLYPHES eux-mêmes (fillText), pas des cercles. Le texte reste lisible
   mais ondule. Combine bien avec scatter/multiply pour effet "liquide
   sous vent".

   Performance : ~5-10k caractères max (grille 100×60) à 60fps. Pour grilles
   plus denses, baisser density ou augmenter font size.
   ============================================================================ */

import { useEffect, useRef } from 'react';
import type { CanvasParams } from '../../lib/canvasStore';

/** Palette ID → hex (duplique CreationCanvas.paletteToHex pour éviter circular dep). */
function paletteToHex(id: CanvasParams['palette']): string {
  switch (id) {
    case 'mono-ink': return '#0d0d0d';
    case 'silver': return '#b8b8b8';
    case 'graphite': return '#2c2c2c';
    case 'stone': return '#8a8a8a';
    case 'ash': return '#5a5a5a';
    default: return '#0d0d0d';
  }
}

interface Props {
  grid: string[][];
  params: CanvasParams;
}

export function LiquidLayer({ grid, params }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // On stocke grid + params dans des refs pour que le RAF puisse les lire
  // sans redéclencher l'effet à chaque change (RAF setup une seule fois)
  const gridRef = useRef(grid);
  const paramsRef = useRef(params);
  gridRef.current = grid;
  paramsRef.current = params;

  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    let startTime = performance.now();

    // Respect prefers-reduced-motion → animation off (rendu statique)
    const prefersReducedMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches ?? false;

    function tick() {
      const cur = canvasRef.current;
      if (!cur || !ctx) return;

      const dpr = window.devicePixelRatio || 1;
      const rect = cur.getBoundingClientRect();
      if (cur.width !== rect.width * dpr || cur.height !== rect.height * dpr) {
        cur.width = Math.max(1, Math.round(rect.width * dpr));
        cur.height = Math.max(1, Math.round(rect.height * dpr));
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, rect.width, rect.height);

      const g = gridRef.current;
      const p = paramsRef.current;
      if (g.length === 0) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const cols = g[0]?.length ?? 0;
      const rows = g.length;
      if (cols === 0) {
        raf = requestAnimationFrame(tick);
        return;
      }

      const fs = p.fontSize;
      const charW = fs * 0.6 + p.letterSpacing;
      const lh = fs * p.lineHeight;
      const blockW = cols * charW;
      const blockH = rows * lh;
      const ox = (rect.width - blockW) / 2;
      const oy = (rect.height - blockH) / 2;

      let fill = paletteToHex(p.palette);
      if (p.bgPalette === 'ink' && p.palette === 'mono-ink') {
        fill = '#ffffff';
      }
      ctx.fillStyle = fill;
      ctx.font = `${fs}px "JetBrains Mono", ui-monospace, monospace`;
      ctx.textBaseline = 'top';

      // Temps en secondes pour les fonctions sin/cos
      const t = prefersReducedMotion ? 0 : (performance.now() - startTime) * 0.001;

      // Paramètres de la wave — amplitude proportionnelle au fontSize pour
      // garder un effet cohérent quel que soit le zoom. Fréquences spatiales
      // basses pour propagation lente le long de la grille (effet "vague").
      const ampX = fs * 0.7;
      const ampY = fs * 0.5;
      const freqX = 0.18;
      const freqY = 0.22;
      const timeFreqX = 1.2;
      const timeFreqY = 0.85;

      for (let r = 0; r < rows; r++) {
        const row = g[r]!;
        const baseY = oy + r * lh;
        for (let c = 0; c < cols; c++) {
          const ch = row[c];
          if (!ch || ch === ' ') continue;
          const baseX = ox + c * charW;
          // Wave : combinaison sin/cos avec phases spatiales (r, c) et phase
          // temporelle (t). Les fréquences relatives X/Y créent un mouvement
          // organique non-périodique.
          const wx = Math.sin(t * timeFreqX + r * freqY + c * freqX) * ampX;
          const wy = Math.cos(t * timeFreqY + r * freqX + c * freqY) * ampY;
          ctx.fillText(ch, baseX + wx, baseY + wy);
        }
      }

      if (!prefersReducedMotion) {
        raf = requestAnimationFrame(tick);
      }
    }

    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
    };
  }, []); // setup une seule fois — la RAF lit gridRef/paramsRef à chaque tick

  return (
    <canvas
      ref={canvasRef}
      className="canvas-stipple"
      data-testid="canvas-liquid"
      aria-hidden="true"
    />
  );
}
